/**
 * 机器人名单页面
 */
import React, { FunctionComponent, useRef, useMemo } from 'react'
import { Card, Table, Tooltip, message, Popconfirm, Button, Space } from 'antd'
import { getTableXwidth } from '@configs/table'
import { ColumnProps } from 'antd/lib/table'
import { CommonFilters, PageHeader, UserAvatar, CallRobot } from '@components'
import { configRobotTaskFilterItems } from '../../../components/CommonFilters/configs'
import { listRobotCallRoll, pauseRobotCallRoll, resumeRobotCallRoll, deleteRobotCallRoll } from '@services/robot'
import { useTableService } from '@hooks/useTableService'
import {
  RobotFilterTypeEnum,
  ListRobotCallRollItem,
  CallRobotSourceEnum,
  RobotTaskStatusEnum,
} from '@types'
import { sourceTypeMap, robotTaskStatusMap } from '@configs/map'
import { cosGetObjectUrl } from '../../../library/cosUtils'
import downloadByATag from '../../../library/downloadByATag'
import { T3CCallRobot } from 'src/components/T3CCallRobot'

const ConfigRobotTaskLogPage: FunctionComponent<React.PropsWithChildren<unknown>> = () => {
  const filterRef = useRef()
  const initFilter = {
    page: 1,
    pageSize: 10,
  }

  const {
    loading,
    list,
    pagination,
    setFilter,
    resetFilter,
    rowKey,
    reload,
  } = useTableService(listRobotCallRoll, initFilter, '获取机器人名单失败')

  const downloadCosFile = async (fileKey) => {
    const [url, err] = await cosGetObjectUrl(fileKey) // 这里的key要和cosPutObject时的key匹配
    if (err) {
      message.error(`下载失败: ${err.message}`)
    } else {
      downloadByATag(fileKey, url)
    }
  }

  // 暂停
  const handlePause = async (rollId: number) => {
    const [, err] = await pauseRobotCallRoll({ rollId })
    if (err) {
      message.error(`暂停失败: ${err.message}`)
      return
    }
    message.success('已暂停')
    reload()
  }

  // 恢复
  const handleResume = async (rollId: number) => {
    const [, err] = await resumeRobotCallRoll({ rollId })
    if (err) {
      message.error(`恢复失败: ${err.message}`)
      return
    }
    message.success('已恢复')
    reload()
  }

  // 删除
  const handleDelete = async (rollId: number) => {
    const [, err] = await deleteRobotCallRoll({ rollId })
    if (err) {
      message.error(`删除失败: ${err.message}`)
      return
    }
    message.success('已删除')
    reload()
  }

  const columns: ColumnProps<ListRobotCallRollItem>[] = useMemo(() => [
    { title: '名单id', dataIndex: 'id', width: 80 },
    { title: '名单标题', dataIndex: 'title', width: 150 },
    {
      title: '名单状态',
      dataIndex: 'status',
      render: status => robotTaskStatusMap[status],
      width: 80,
    },
    {
      title: '名单来源',
      dataIndex: 'sourceType',
      render: sourceType => sourceTypeMap[sourceType],
      width: 80,
    },
    { title: '名单描述', dataIndex: 'description', width: 150, ellipsis: true },
    { title: '名单详情', dataIndex: 'remark', width: 150, ellipsis: true },
    {
      title: '起止时间',
      dataIndex: 'taskStartTime',
      width: 170,
      render: (_, record) => {
        if (!record?.taskStartTime && !record?.taskEndTime) return '-'
        return <div>{record?.taskStartTime || '-'}<br />~ {record?.taskEndTime || '-'}</div>
      },
    },
    {
      title: '呼出时段',
      dataIndex: 'availableTime',
      width: 150,
      render: (availableTime: ListRobotCallRollItem['availableTime']) => {
        if (!availableTime?.length) return '-'
        return (
          <div>
            {availableTime.map((item, index) => (
              <div key={index}>{item.startTime} ~ {item.endTime}</div>
            ))}
          </div>
        )
      },
    },
    {
      title: '列表内线索创建时间',
      dataIndex: 'leadCreateTimeRange',
      width: 180,
      render: (value: string) => value || '-',
    },
    {
      title: '创建人',
      dataIndex: 'creator',
      width: 150,
      render: value => <UserAvatar salesName={value} />,
    },
    { title: '创建时间', dataIndex: 'createTime', width: 150 },
    {
      title: '其他信息',
      dataIndex: 'uploadDesc',
      width: 80,
      render: (value, record) => (
        <Tooltip
          color="#fafafa"
          placement="topLeft"
          title={
            <div style={{ color: '#000', padding: 10 }}>
              {value}<br />
              {record?.filterCondition && <a onClick={() => downloadCosFile(record?.filterCondition)}>查看原上传文件</a>}<br />
              {record?.failCidFilePath && <a onClick={() => downloadCosFile(record?.failCidFilePath)}>查看失败原因</a>}
            </div>
          }
        >
          {record?.sourceType === CallRobotSourceEnum.CALL_RECORD && <a>上传详情</a>}
        </Tooltip>
      ),
    },
    {
      title: '操作',
      dataIndex: 'operate',
      width: 160,
      fixed: 'right',
      render: (_, record) => {
        // 进行中（SUCCEED）可暂停；已暂停（PAUSED）可启动；删除任意状态可点击
        const canPause = record?.status === RobotTaskStatusEnum.SUCCEED
        const canResume = record?.status === RobotTaskStatusEnum.PAUSED
        return (
          <Space>
            {canPause && (
              <Button type="link" size="small" style={{ padding: 0, color: '#fa8c16' }} onClick={() => handlePause(record.id)}>暂停</Button>
            )}
            {canResume && (
              <Button type="link" size="small" style={{ padding: 0, color: '#52c41a' }} onClick={() => handleResume(record.id)}>启动</Button>
            )}
            <Popconfirm
              title="删除后任务不再呼出且不可恢复，建议进行中任务先暂停再删除。确认删除？"
              okText="确认删除"
              cancelText="取消"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button type="link" size="small" danger style={{ padding: 0 }}>删除</Button>
            </Popconfirm>
          </Space>
        )
      },
    },
  ], [])
  const tableXWidth = useMemo(() => getTableXwidth(columns), [columns])

  return (
    <>
      <PageHeader title="机器人名单" />
      <Card className="mt20">
        <CommonFilters
          cRef={filterRef}
          className="custom-Label"
          colProps={{ flex: '1 1 300px' }}
          itemList={configRobotTaskFilterItems}
          onSubmit={filter => setFilter({ ...filter, page: 1 })}
          onReset={() => resetFilter(initFilter)}
        />
      </Card>
      <Card className="mt20">
        <CallRobot
          reload={() => reload()}
          sourceType={CallRobotSourceEnum.CALL_RECORD}
          filterType={RobotFilterTypeEnum.BATCH_UPLOAD}
        />
        <T3CCallRobot
          reload={() => reload()}
          sourceType={CallRobotSourceEnum.CALL_RECORD}
          filterType={RobotFilterTypeEnum.BATCH_UPLOAD}
        />
        <Table
          loading={loading}
          scroll={{ x: tableXWidth }}
          rowKey={rowKey}
          onChange={() => console.log('change')}
          dataSource={list}
          columns={columns}
          pagination={pagination}
          tableLayout="fixed"
          className="mt20"
        />
      </Card>
    </>
  )
}

export default ConfigRobotTaskLogPage
